import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Add, ArrowDownward, ArrowUpward, Chat, DeleteOutline, Edit, Notes, Search } from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import KanbanTicketDetails from "../../components/KanbanTicketDetails";

const priorityOptions = [
  { id: "low", name: "Baixa" },
  { id: "normal", name: "Normal" },
  { id: "high", name: "Alta" },
  { id: "urgent", name: "Urgente" }
];
const priorityColors = { low: "#6B7280", normal: "#3B82F6", high: "#F59E0B", urgent: "#DC2626" };
const slaLabels = { breached: "SLA vencido", warning: "SLA próximo", ok: "SLA no prazo", waiting_customer: "Aguardando cliente" };
const slaColors = { breached: "#DC2626", warning: "#F59E0B", ok: "#10B981", waiting_customer: "#6B7280" };

const useStyles = makeStyles(theme => ({
  root: { overflow: "hidden" },
  filters: { display: "flex", gap: 8, padding: theme.spacing(1), flexWrap: "wrap", alignItems: "center" },
  filter: { minWidth: 150 },
  board: { display: "flex", flex: 1, width: "100%", minHeight: 0, gap: 12, padding: 12, paddingBottom: 22, overflowX: "scroll", overflowY: "hidden", alignItems: "stretch" },
  column: { minWidth: 300, width: 300, flexShrink: 0, maxHeight: "calc(100vh - 170px)", overflowY: "auto", padding: 8, background: theme.palette.background.default },
  columnHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 4px 10px", position: "sticky", top: 0, background: theme.palette.background.default, zIndex: 1 },
  card: { marginBottom: 8, cursor: "grab", borderLeft: "5px solid" },
  contact: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  details: { display: "flex", justifyContent: "space-between", alignItems: "center", color: theme.palette.text.secondary },
  cardActions: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 },
  stageSelect: { minWidth: 145, fontSize: 12 },
  columnRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  chip: { height: 20, color: "#fff", marginRight: 3, marginTop: 5, fontSize: 11 }
}));

const MultiFilter = ({ placeholder, items, value, onChange, includeUnassigned }) => (
  <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
    <Select
      multiple
      displayEmpty
      value={value}
      onChange={event => onChange(event.target.value)}
      renderValue={() => placeholder}
    >
      {includeUnassigned && <MenuItem value={0}>
        <Checkbox checked={value.indexOf(0) > -1} />
        <ListItemText primary={i18n.t("kanban.filters.unassigned")} />
      </MenuItem>}
      {items.map(item => <MenuItem key={item.id} value={item.id}>
        <Checkbox checked={value.indexOf(item.id) > -1} style={item.color ? { color: item.color } : undefined} />
        <ListItemText primary={item.name} />
      </MenuItem>)}
    </Select>
  </FormControl>
);

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [columns, setColumns] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldId, setCustomFieldId] = useState("");
  const [customFieldValue, setCustomFieldValue] = useState("");
  const [search, setSearch] = useState("");
  const [draggedTicketId, setDraggedTicketId] = useState(null);
  const [detailsTicket, setDetailsTicket] = useState(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [columnEdit, setColumnEdit] = useState(null);
  const [columnName, setColumnName] = useState("");
  const [columnColor, setColumnColor] = useState("#3B82F6");
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessSearch, setAccessSearch] = useState("");
  const [accessResults, setAccessResults] = useState([]);

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/kanban/tickets", {
        params: {
          queueIds: JSON.stringify(queueIds),
          userIds: JSON.stringify(userIds),
          tagIds: JSON.stringify(tagIds),
          priorities: JSON.stringify(priorities),
          customFieldId: customFieldId || undefined,
          customFieldValue: customFieldValue || undefined,
          search
        }
      });
      setTickets(data);
    } catch (err) { toastError(err); }
  };

  useEffect(() => {
    Promise.all([api.get("/kanban/columns"), api.get("/queue"), api.get("/tags"), api.get("/users"), api.get("/ticket-custom-fields")])
      .then(([columnResult, queueResult, tagResult, userResult, customFieldsResult]) => {
        setColumns(columnResult.data.filter(column => column.active));
        setQueues(queueResult.data);
        setTags(tagResult.data);
        setUsers(userResult.data.users);
        setCustomFields(customFieldsResult.data.filter(field => field.active));
      }).catch(toastError);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(loadTickets, 350);
    return () => clearTimeout(timeout);
  }, [queueIds, userIds, tagIds, priorities, customFieldId, customFieldValue, search]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("ticket", data => {
      if (data.action !== "update") return;
      setTickets(current => {
        const exists = current.some(ticket => ticket.id === data.ticket.id);
        return exists
          ? current.map(ticket => ticket.id === data.ticket.id ? data.ticket : ticket)
          : current;
      });
    });
    return () => socket.disconnect();
  }, []);

  const move = async (ticketId, columnId) => {
    if (!ticketId) return;
    const previous = tickets;
    setTickets(current => current.map(ticket => ticket.id === ticketId ? { ...ticket, kanbanColumnId: columnId } : ticket));
    try {
      const { data } = await api.put(`/kanban/tickets/${ticketId}`, { columnId });
      setTickets(current => current.map(ticket => ticket.id === ticketId ? data : ticket));
    } catch (err) {
      setTickets(previous);
      toastError(err);
    }
  };

  const updateTicket = updated => {
    setTickets(current => current.map(ticket => ticket.id === updated.id ? updated : ticket));
    setDetailsTicket(updated);
  };

  const openDetails = async ticketId => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setDetailsTicket(data);
    } catch (err) { toastError(err); }
  };

  const saveColumn = async () => {
    try {
      const payload = {
        name: columnName,
        color: columnColor,
        position: columnEdit ? columnEdit.position : columns.length,
        active: true
      };
      if (columnEdit) await api.put(`/kanban/columns/${columnEdit.id}`, payload);
      else await api.post("/kanban/columns", payload);
      const { data } = await api.get("/kanban/columns");
      setColumns(data.filter(column => column.active));
      setColumnEdit(null); setColumnName(""); setColumnColor("#3B82F6");
    } catch (err) { toastError(err); }
  };

  const editColumn = column => {
    setColumnEdit(column); setColumnName(column.name); setColumnColor(column.color);
  };

  const archiveColumn = async column => {
    try {
      await api.delete(`/kanban/columns/${column.id}`);
      setColumns(current => current.filter(item => item.id !== column.id));
    } catch (err) { toastError(err); }
  };

  const reorderColumn = async (column, direction) => {
    const index = columns.findIndex(item => item.id === column.id);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columns.length) return;
    const reordered = [...columns];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const normalized = reordered.map((item, position) => ({ ...item, position }));
    setColumns(normalized);
    try {
      await Promise.all(normalized.map(item => api.put(`/kanban/columns/${item.id}`, {
        name: item.name,
        color: item.color,
        position: item.position,
        active: item.active
      })));
    } catch (err) { toastError(err); }
  };

  const discover = async () => {
    try {
      const { data } = await api.get("/ticket-access/discover", { params: { search: accessSearch } });
      setAccessResults(data);
    } catch (err) { toastError(err); }
  };

  const requestAccess = async ticketId => {
    try {
      await api.post(`/tickets/${ticketId}/access-request`);
      setAccessResults(current => current.map(item => item.id === ticketId ? { ...item, accessStatus: "pending" } : item));
    } catch (err) { toastError(err); }
  };

  return (
    <MainContainer className={classes.root}>
      <MainHeader><Title>{i18n.t("kanban.title")}</Title></MainHeader>
      <Paper className={classes.filters} variant="outlined">
        <TextField
          size="small"
          variant="outlined"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={i18n.t("kanban.filters.search")}
          InputProps={{ startAdornment: <Search fontSize="small" /> }}
        />
        <MultiFilter placeholder={i18n.t("kanban.filters.queues")} items={queues} value={queueIds} onChange={setQueueIds} />
        {user.profile === "admin" && <MultiFilter placeholder={i18n.t("kanban.filters.users")} items={users} value={userIds} onChange={setUserIds} includeUnassigned />}
        <MultiFilter placeholder={i18n.t("kanban.filters.tags")} items={tags} value={tagIds} onChange={setTagIds} />
        <MultiFilter placeholder="Prioridades" items={priorityOptions} value={priorities} onChange={setPriorities} />
        <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Campo</InputLabel>
          <Select value={customFieldId} onChange={event => setCustomFieldId(event.target.value)} label="Campo">
            <MenuItem value="">Nenhum</MenuItem>
            {customFields.map(field => <MenuItem key={field.id} value={field.id}>{field.name}</MenuItem>)}
          </Select>
        </FormControl>
        {customFieldId && <TextField size="small" variant="outlined" value={customFieldValue} onChange={event => setCustomFieldValue(event.target.value)} placeholder="Valor do campo" />}
        <Button variant="outlined" onClick={() => setAccessOpen(true)}>{i18n.t("kanban.requestAccess")}</Button>
        {user.profile === "admin" && <Button variant="outlined" color="primary" onClick={() => setColumnsOpen(true)}>{i18n.t("kanban.manageColumns")}</Button>}
      </Paper>
      <div className={classes.board}>
        {columns.map(column => {
          const cards = tickets.filter(ticket => ticket.kanbanColumnId === column.id);
          return <Paper
            key={column.id}
            className={classes.column}
            onDragOver={event => event.preventDefault()}
            onDragEnter={event => event.preventDefault()}
            onDrop={event => {
              event.preventDefault();
              move(draggedTicketId || Number(event.dataTransfer.getData("ticketId")), column.id);
              setDraggedTicketId(null);
            }}
          >
            <div className={classes.columnHeader}>
              <Typography variant="subtitle1" style={{ color: column.color, fontWeight: 700 }}>{column.name}</Typography>
              <Chip size="small" label={cards.length} />
            </div>
            {cards.map(ticket => <Card
              key={ticket.id}
              draggable
              className={classes.card}
              style={{ borderLeftColor: column.color }}
              onDragStart={event => {
                setDraggedTicketId(ticket.id);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("ticketId", String(ticket.id));
              }}
              onDragEnd={() => setDraggedTicketId(null)}
            >
              <CardContent>
                <div className={classes.contact}>
                  <Avatar src={ticket.contact?.profilePicUrl} />
                  <div><Typography variant="subtitle2">{ticket.contact?.name}</Typography><Typography variant="caption">#{ticket.id}</Typography></div>
                </div>
                <Typography variant="body2" noWrap>{ticket.lastMessage || i18n.t("kanban.noMessage")}</Typography>
                <Chip size="small" label={priorityOptions.find(item => item.id === ticket.priority)?.name || "Normal"} style={{ marginTop: 5, color: "#fff", backgroundColor: priorityColors[ticket.priority || "normal"] }} />
                {ticket.sla?.active && <Chip size="small" label={`${slaLabels[ticket.sla.status]}${ticket.sla.remainingMinutes != null ? ` (${Math.abs(ticket.sla.remainingMinutes)} min)` : ""}`} style={{ marginTop: 5, marginLeft: 4, color: "#fff", backgroundColor: slaColors[ticket.sla.status] }} />}
                <div>{(ticket.tags || []).map(tag => <Chip key={tag.id} className={classes.chip} label={tag.name} style={{ backgroundColor: tag.color }} />)}</div>
                <div className={classes.details}>
                  <Typography variant="caption">{ticket.user?.name || i18n.t("kanban.filters.unassigned")}</Typography>
                  <Typography variant="caption">{ticket.queue?.name || ""}</Typography>
                </div>
                <div className={classes.cardActions}>
                  <FormControl className={classes.stageSelect} size="small">
                    <InputLabel>{i18n.t("kanban.stage")}</InputLabel>
                    <Select value={ticket.kanbanColumnId || ""} onChange={event => move(ticket.id, event.target.value)}>
                      {columns.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <div>
                    <Tooltip title={i18n.t("kanban.details")}><IconButton size="small" onClick={() => openDetails(ticket.id)}><Notes /></IconButton></Tooltip>
                    <Tooltip title={i18n.t("kanban.openConversation")}><IconButton size="small" onClick={() => history.push(`/tickets/${ticket.id}`)}><Chat /></IconButton></Tooltip>
                  </div>
                </div>
              </CardContent>
            </Card>)}
          </Paper>;
        })}
      </div>
      <KanbanTicketDetails open={Boolean(detailsTicket)} onClose={() => setDetailsTicket(null)} ticket={detailsTicket} users={users} onUpdate={updateTicket} />
      <Dialog open={columnsOpen} onClose={() => setColumnsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("kanban.columns.title")}</DialogTitle>
        <DialogContent dividers>
          {columns.map(column => <div className={classes.columnRow} key={column.id}>
            <span style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: column.color }} />
            <Typography style={{ flex: 1 }}>{column.name}</Typography>
            <IconButton size="small" onClick={() => reorderColumn(column, -1)}><ArrowUpward /></IconButton>
            <IconButton size="small" onClick={() => reorderColumn(column, 1)}><ArrowDownward /></IconButton>
            <IconButton size="small" onClick={() => editColumn(column)}><Edit /></IconButton>
            <IconButton size="small" onClick={() => archiveColumn(column)}><DeleteOutline /></IconButton>
          </div>)}
          <div className={classes.columnRow}>
            <TextField fullWidth label={i18n.t("kanban.columns.name")} value={columnName} onChange={event => setColumnName(event.target.value)} />
            <TextField type="color" value={columnColor} onChange={event => setColumnColor(event.target.value)} />
            <IconButton color="primary" disabled={!columnName.trim()} onClick={saveColumn}><Add /></IconButton>
          </div>
        </DialogContent>
        <DialogActions><Button onClick={() => setColumnsOpen(false)}>{i18n.t("kanbanDetails.close")}</Button></DialogActions>
      </Dialog>
      <Dialog open={accessOpen} onClose={() => setAccessOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("kanban.access.title")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.columnRow}>
            <TextField fullWidth variant="outlined" value={accessSearch} onChange={event => setAccessSearch(event.target.value)} placeholder={i18n.t("kanban.filters.search")} />
            <Button variant="contained" color="primary" onClick={discover}>{i18n.t("kanban.access.search")}</Button>
          </div>
          {accessResults.map(item => <div className={classes.columnRow} key={item.id}>
            <Avatar src={item.contact?.profilePicUrl} />
            <div style={{ flex: 1 }}><Typography>{item.contact?.name}</Typography><Typography variant="caption">{item.contact?.number}</Typography></div>
            {item.hasAccess
              ? <Button onClick={() => history.push(`/tickets/${item.id}`)}>{i18n.t("kanban.access.open")}</Button>
              : <Button disabled={item.accessStatus === "pending"} onClick={() => requestAccess(item.id)}>{item.accessStatus === "pending" ? i18n.t("kanban.access.pending") : i18n.t("kanban.access.request")}</Button>}
          </div>)}
        </DialogContent>
        <DialogActions><Button onClick={() => setAccessOpen(false)}>{i18n.t("kanbanDetails.close")}</Button></DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Kanban;
